import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Modal,
  Input,
  Form,
  InputNumber,
  Button,
  Select,
  DatePicker,
  Space,
  Divider
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

const PatchModalExemption = ({
  data,
  patchContractValue,
  modalModifExemption,
  setModalModifExemption,
  setKey,
  modalKey,
  supplier
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState(null);

  const handleChange = ({ sites }, values) => {
    if (sites) {
      setFormValues({
        ...formValues,
        sites: (values?.sites || sites).map((s) => ({
          ...s
        }))
      });
      form.setFieldsValue({
        sites: (values?.sites || sites).map((s) => ({
          ...s
        }))
      });
    }
  };

  useEffect(() => {
    (async () => {
      await setFormValues({ [modalKey]: data });
    })();
    // eslint-disable-next-line
  }, [data]);

  return (
    <>
      <Modal
        title={t(`settings.tickets.modal_exemption.${modalKey}`)}
        centered
        open={modalModifExemption}
        onCancel={() => {
          setModalModifExemption(false);
          setKey();
        }}
        width={750}
        footer={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={patchContractValue}
          onValuesChange={handleChange}
        >
          <Form.List name={modalKey} initialValue={data}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => {
                  return (
                    <div key={field.name}>
                      {modalKey && modalKey !== 'claims' && (
                        <>
                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'number']}
                            key={[field.name, 'address', 'number']}
                            label={t('contracts.form.address.number')}
                            rules={[
                              {
                                required: true,
                                message: t('errors.form.REQUIRED')
                              }
                            ]}
                          >
                            <InputNumber />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'street']}
                            key={[field.name, 'address', 'street']}
                            label={t('contracts.form.address.street')}
                            rules={[
                              {
                                required: true,
                                message: t('errors.form.REQUIRED')
                              }
                            ]}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'additional']}
                            key={[field.name, 'address', 'additional']}
                            label={t('contracts.form.address.additional')}
                          >
                            <Input />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'postal_code']}
                            key={[field.name, 'address', 'postal_code']}
                            label={t('contracts.form.address.postal_code')}
                            rules={[
                              {
                                required: true,
                                message: t('errors.form.REQUIRED')
                              }
                            ]}
                          >
                            <Input
                              style={{ width: '90px' }}
                              autoComplete="off"
                            />
                          </Form.Item>

                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'commune_code']}
                            key={[field.name, 'address', 'commune_code']}
                            label={t('contracts.form.commune_code')}
                            hidden
                          >
                            <Input disabled style={{ width: '90px' }} />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'address', 'city']}
                            key={[field.name, 'address', 'city']}
                            label={t('contracts.form.address.city')}
                            rules={[
                              {
                                required: true,
                                message: t('errors.form.REQUIRED')
                              }
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </>
                      )}
                      {modalKey && modalKey === 'buildings' && (
                        <>
                          <Form.Item
                            {...field}
                            name={[field.name, 'building_name']}
                            key={[field.name, 'building_name']}
                            label={t('contracts.form.building_name')}
                          >
                            <Input />
                          </Form.Item>
                          <Form.Item
                            {...field}
                            name={[field.name, 'building_siret']}
                            key={[field.name, 'building_siret']}
                            label={t('contracts.form.building_siret')}
                          >
                            <Input min={0} minLength={9} maxLength={14} />
                          </Form.Item>
                        </>
                      )}
                      {modalKey && modalKey === 'sites' && (
                        <>
                          <Form.Item
                            {...field}
                            name={[field.name, 'resident_status']}
                            key={[field.name, 'resident_status']}
                            label={t('contracts.form.resident_status')}
                          >
                            <Select>
                              <Option value="OWNER">
                                {t('contracts.form.options.OWNER')}
                              </Option>
                              <Option value="TENANT">
                                {t('contracts.form.options.TENANT')}
                              </Option>
                              <Option value="RENT_FREE">
                                {t('contracts.form.options.RENT_FREE')}
                              </Option>
                              {supplier === 'ml' && (
                                <>
                                  <Option value="SUB_TENANT">
                                    {t('contracts.form.options.SUB_TENANT')}
                                  </Option>
                                  <Option value="HOSTED_BY_TENANT">
                                    {t(
                                      'contracts.form.options.HOSTED_BY_TENANT'
                                    )}
                                  </Option>
                                </>
                              )}
                            </Select>
                          </Form.Item>
                          {formValues?.sites?.length &&
                            ['TENANT'].includes(
                              formValues?.sites[field.name]?.resident_status
                            ) && (
                              <Form.Item
                                {...field}
                                name={[field.name, 'tenant_status']}
                                key={[field.name, 'tenant_status']}
                                label={t('contracts.form.tenant_status')}
                                rules={[{ required: true }]}
                              >
                                <Select>
                                  <>
                                    <Option value="TENANT_AGAINST_OWNER">
                                      {t(
                                        'contracts.form.options.TENANT_AGAINST_OWNER'
                                      )}
                                    </Option>
                                    <Option value="OWNER_AGAINST_TENANT">
                                      {t(
                                        'contracts.form.options.OWNER_AGAINST_TENANT'
                                      )}
                                    </Option>
                                    <Option value="NO_AGAINST">
                                      {t('contracts.form.options.NO_AGAINST')}
                                    </Option>
                                  </>
                                  <Option value="OWNER_ACTING_TENANT">
                                    {t(
                                      'contracts.form.options.OWNER_ACTING_TENANT'
                                    )}
                                  </Option>
                                  <Option value="WITHOUT_CLAUSE">
                                    {t('contracts.form.options.WITHOUT_CLAUSE')}
                                  </Option>
                                </Select>
                              </Form.Item>
                            )}
                          <Form.Item
                            {...field}
                            name={[field.name, 'surface_area']}
                            key={[field.name, 'surface_area']}
                            label={t('contracts.form.surface_area')}
                          >
                            <InputNumber />
                          </Form.Item>
                        </>
                      )}
                      {modalKey && modalKey === 'claims' && (
                        <>
                          <div style={{ marginBottom: 5 }}>
                            {`Sinistre N°${field.key + 1}:`}
                          </div>
                          <Space
                            key={field.key}
                            style={{ display: 'flex', marginBottom: 8 }}
                            align="baseline"
                          >
                            <Form.Item
                              noStyle
                              {...field}
                              name={[field.name, 'claim_date']}
                              key={[field.name, 'claim_date']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Date du sinistre manquant'
                                }
                              ]}
                            >
                              <DatePicker
                                format="DD/MM/YYYY"
                                style={{ minWidth: 200 }}
                                disabledDate={(current) => {
                                  return current >= moment();
                                }}
                              />
                            </Form.Item>
                            <Form.Item
                              noStyle
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...field}
                              name={[field.name, 'claim_type']}
                              key={[field.name, 'claim_type']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Type du sinistre manquant'
                                }
                              ]}
                            >
                              <Input
                                style={{ width: '200px' }}
                                placeholder="Type de sinistre"
                              />
                            </Form.Item>
                            <Form.Item
                              noStyle
                              // eslint-disable-next-line react/jsx-props-no-spreading
                              {...field}
                              name={[field.name, 'claim_amount']}
                              key={[field.name, 'claim_amount']}
                              rules={[
                                {
                                  required: true,
                                  message: 'Montant du sinistre manquant'
                                }
                              ]}
                            >
                              <InputNumber
                                min={0}
                                style={{ width: '200px' }}
                                formatter={(value) => value && `${value}€`}
                                parser={(value) => value.replace('€', '')}
                                placeholder="Montant du sinistre"
                              />
                            </Form.Item>

                            <MinusCircleOutlined
                              onClick={() => remove(field.name)}
                              style={{
                                color: '#ff4d4f',
                                fontSize: '20px',
                                cursor: 'pointer',
                                marginLeft: 8
                              }}
                            />
                          </Space>
                        </>
                      )}
                      <Divider />
                    </div>
                  );
                })}
                {modalKey === 'claims' && (
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                      style={{ marginTop: 20 }}
                    >
                      Ajouter un sinistre
                    </Button>
                  </Form.Item>
                )}
              </>
            )}
          </Form.List>
          <Form.Item style={{ marginTop: 25, textAlign: 'center' }}>
            <Button type="primary" htmlType="submit">
              {t('buttons.save')}
            </Button>
            <Button
              type="link"
              danger
              onClick={() => {
                setModalModifExemption(false);
                setKey();
              }}
            >
              {t('buttons.cancel')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
PatchModalExemption.propTypes = {
  modalKey: PropTypes.string.isRequired,
  modalModifExemption: PropTypes.bool.isRequired,
  setKey: PropTypes.func.isRequired,
  setModalModifExemption: PropTypes.func.isRequired,
  patchContractValue: PropTypes.func.isRequired,
  data: PropTypes.shape({
    address: PropTypes.shape({})
  }).isRequired,
  supplier: PropTypes.string.isRequired
};

export default PatchModalExemption;
